import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, AlertTriangle, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChargeAudit = () => {
  const [audits, setAudits] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onlyMismatches, setOnlyMismatches] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const params = new URLSearchParams({ limit: 100 });
      if (onlyMismatches) params.append('only_mismatches', 'true');
      const [auditRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/charge-audit?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/charge-audit/summary`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const auditData = await auditRes.json();
      const summaryData = await summaryRes.json();
      setAudits(auditData.audits || []);
      setSummary(summaryData);
    } catch {} finally { setLoading(false); }
  }, [onlyMismatches]);

  useEffect(() => { load(); }, [load]);

  const fmtTime = (iso) => new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const fmt = (n) => n == null ? '—' : `£${Number(n).toFixed(2)}`;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900">Charge Audit Log</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Every charge sent to Stripe or GoCardless, captured at the API call site.
            "Sent" = the literal pence sent. "Expected" = what we'd compute fresh from the invoice.
          </p>
        </div>
        <Button onClick={load} variant="outline" size="sm" disabled={loading}>
          <RefreshCw size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div className="p-3 bg-zinc-50 rounded-xl">
            <p className="text-xs uppercase tracking-wider text-zinc-500">Charges (last 24h)</p>
            <p className="text-2xl font-bold text-zinc-900">{summary.total_charges}</p>
          </div>
          <div className="p-3 bg-emerald-50 rounded-xl">
            <p className="text-xs uppercase tracking-wider text-emerald-600">Total charged</p>
            <p className="text-2xl font-bold text-emerald-700">£{(summary.total_charged_decimal || 0).toFixed(2)}</p>
          </div>
          <div className={`p-3 rounded-xl ${summary.mismatches > 0 ? 'bg-red-50' : 'bg-zinc-50'}`}>
            <p className="text-xs uppercase tracking-wider text-zinc-500">Amount mismatches</p>
            <p className={`text-2xl font-bold ${summary.mismatches > 0 ? 'text-red-700' : 'text-zinc-900'}`}>
              {summary.mismatches}
            </p>
          </div>
          <div className={`p-3 rounded-xl ${summary.failed > 0 ? 'bg-amber-50' : 'bg-zinc-50'}`}>
            <p className="text-xs uppercase tracking-wider text-zinc-500">Failed charges</p>
            <p className={`text-2xl font-bold ${summary.failed > 0 ? 'text-amber-700' : 'text-zinc-900'}`}>
              {summary.failed}
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
          <input type="checkbox" checked={onlyMismatches} onChange={(e) => setOnlyMismatches(e.target.checked)} />
          Show only mismatches
        </label>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-left text-xs uppercase text-zinc-500">
              <th className="p-3">Time</th>
              <th className="p-3">Customer</th>
              <th className="p-3">Method</th>
              <th className="p-3">Source</th>
              <th className="p-3 text-right">Sent</th>
              <th className="p-3 text-right">Expected</th>
              <th className="p-3 text-right">Δ</th>
              <th className="p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {audits.length === 0 && !loading && (
              <tr><td colSpan={8} className="p-6 text-center text-zinc-500">No charges in the audit log yet.</td></tr>
            )}
            {audits.map(a => {
              const mismatch = a.mismatch === true;
              return (
                <tr key={a.id} className={`border-t border-zinc-100 ${mismatch ? 'bg-red-50' : ''}`}>
                  <td className="p-3 text-xs text-zinc-500 whitespace-nowrap">{fmtTime(a.created_at)}</td>
                  <td className="p-3">
                    <p className="font-medium text-zinc-900">{a.customer_name || '—'}</p>
                    {a.invoice_number && <p className="text-xs text-zinc-500">Inv {a.invoice_number}</p>}
                  </td>
                  <td className="p-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${a.method === 'stripe' ? 'bg-purple-100 text-purple-700' : 'bg-teal-100 text-teal-700'}`}>
                      {a.method}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-zinc-500">{a.source}</td>
                  <td className="p-3 text-right font-mono">{fmt(a.amount_decimal_sent)}</td>
                  <td className="p-3 text-right font-mono text-zinc-500">{fmt(a.expected_decimal)}</td>
                  <td className={`p-3 text-right font-mono text-xs ${mismatch ? 'text-red-700 font-bold' : 'text-zinc-400'}`}>
                    {a.mismatch_pence ? (a.mismatch_pence > 0 ? '+' : '') + a.mismatch_pence + 'p' : '—'}
                  </td>
                  <td className="p-3">
                    {mismatch && <AlertTriangle size={16} className="text-red-600 inline mr-1" />}
                    {a.ok === true && <CheckCircle size={16} className="text-emerald-600 inline" />}
                    {a.ok === false && <XCircle size={16} className="text-red-600 inline" title={a.error} />}
                    {a.phase === 'attempt' && <Loader2 size={14} className="text-zinc-400 inline animate-spin" />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ChargeAudit;
